import React, { useState, useEffect, useCallback, useReducer, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Toolbar from './Toolbar'
import { SecondaryButton } from 'components/Shared/Button'
import moment from 'moment'
import { useEditEventSeriesContext } from '../EditEventSeriesProvider'
import Calendar from 'components/Shared/Calendar'
import { useOutsideCheckClick } from 'hooks/UseOnClickOutside'
import { fetchEvents } from 'api/events'
import { arrayReducer, initStateArray } from 'reducers/default'
import Paginator from 'components/Shared/Paginator'
import EventItem from './EventItem'
import SearchbarInput from 'components/Shared/Searchbar'
import searchLogo from 'images/icons/search_blue.svg'
import classNames from 'classnames'
import ListLoader from './ListLoader'
import { RemoveIcon } from 'images'

export default function FilterTab() {
  const { t } = useTranslation()

  const [eventsState, dispatchEvents] = useReducer(arrayReducer, initStateArray)
  const [page, setPage] = useState(1)
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [dates, setDates] = useState({
    from: '',
    to: '',
  })
  const [search, setSearch] = useState('')

  const prevSearchRef = useRef(search)
  const prevDatesRef = useRef({ from: dates.from, to: dates.to })
  const isInitializedRef = useRef(false)
  
  const isDateChanged = dates.from && dates.to
  
  // Wrapper around dispatch: for FilterTab we overwrite items when a page is loaded
  // but do not clear them with the final UPDATE([]) coming from fetchEvents
  const filterDispatch = useCallback(
    (action) => {
      if (
        action?.type === 'UPDATE' &&
        action.payload &&
        !Array.isArray(action.payload) &&
        action.payload.items
      ) {
        // main response with { total, maxPage, items, page }
        dispatchEvents({ type: 'OVERWRITE', payload: action.payload })
      } else {
        // for UPDATE([]) and all other actions behave as usual
        dispatchEvents(action)
      }
    },
    [dispatchEvents],
  )

  const { clearSelection, isSelecting, registerFilterTabEvents, isEventIncluded } =
    useEditEventSeriesContext()

  const calendarRef = useRef(null)
  useOutsideCheckClick(calendarRef, setIsCalendarOpen, isCalendarOpen, () => null)

  useEffect(() => {
    if (!(dates.from && dates.to) && !search) {
      return
    }

    // determine if search or dates have changed (not page)
    const searchChanged = prevSearchRef.current !== search
    const datesChanged = 
      prevDatesRef.current.from !== dates.from || 
      prevDatesRef.current.to !== dates.to

    // if this is the first initialization or search/dates have changed, this is the first load
    // if only page has changed, this is not the first load
    const shouldBeFirstLoad = !isInitializedRef.current || searchChanged || datesChanged

    fetchEvents(
      filterDispatch,
      {
        page,
        search,
        since: dates.from,
        until: dates.to,
        exclude_products: true,
        include_archived: false,
        frontend_status: ['DEFAULT', 'FLAGGED'],
        flagged_to_top: false,
        limit: 10,
        descending: false,
      },
      !shouldBeFirstLoad,
    )

    // update refs after fetchEvents
    prevSearchRef.current = search
    prevDatesRef.current = { from: dates.from, to: dates.to }
    isInitializedRef.current = true
  }, [page, search, dates.from, dates.to, filterDispatch])

  useEffect(() => {
    if (eventsState.items) {
      registerFilterTabEvents(eventsState.items)
    }
  }, [eventsState.items, registerFilterTabEvents])

  const handleFromChange = useCallback(
    (from) => {
      setDates((prevDates) => {
        const nextDates = { ...prevDates, from }
        if (nextDates.from && nextDates.to) {
          setPage(1)
        }
        return nextDates
      })
    },
    [],
  )

  const handleToChange = useCallback(
    (to) => {
      setDates((prevDates) => {
        const nextDates = { ...prevDates, to }
        if (nextDates.from && nextDates.to) {
          setPage(1)
        }
        return nextDates
      })
    },
    [],
  )

  const handlePageChange = (newPage) => {
    setPage(newPage)
  }

  const handleSearch = useCallback(
    (q) => {
      setPage(1)
      setSearch(q)
    },
    [],
  )

  const handleClearDates = useCallback(
    (e) => {
      e.stopPropagation()
      setDates({
        from: '',
        to: '',
      })
      setPage(1)
    },
    [],
  )

  useEffect(() => {
    return () => {
      clearSelection()
    }
  }, [clearSelection])

  // return
  return (
    <div>
      <p className='filter-tab_description'>{t('Event.editEventSeriesModal.filterDescription')}</p>

      <div className='filter-tab_wrapper'>
        <Toolbar
          events={eventsState.items}
          className='toolbar_filter-tab'
          isDisabled={eventsState.items.length === 0}
        />

        <div className='filter-tab_filters'>
          <div className='filter-tab_search'>
            <SearchbarInput
              defaultValue={search}
              handleChange={handleSearch}
              placeholder={t('Events.search.placeholder')}
              ariaLabel={t('Events.search.ariaLabel')}
              postfixView={
                <img
                  src={searchLogo}
                  className="searchbar-comp_icon"
                  alt={t('Events.search.placeholder')}
                />
              }
            />
          </div>

          <div className='filter-tab_date-picker' ref={calendarRef}>
            {isCalendarOpen && (
              <div className='event-control-panel_dropdown-menu_dates_row'>
                <div className="event-control-panel_dropdown-menu_dates_row_calendar_left">
                  <Calendar
                    handleDateChange={handleFromChange}
                    maxDate={dates.to || null}
                    isPast={true}
                    isFuture={false}
                    fromDate={dates.from}
                    toDate={dates.to}
                    defaultDate={dates.from ? moment(dates.from, 'DD-MM-YYYY') : moment()}
                  />
                </div>
                <div className="event-control-panel_dropdown-menu_dates_row_calendar_right">
                  <Calendar
                    type="to"
                    handleDateChange={handleToChange}
                    minDate={dates.from || null}
                    isPast={false}
                    isFuture={true}
                    fromDate={dates.from}
                    toDate={dates.to}
                    defaultDate={dates.to ? moment(dates.to, 'DD-MM-YYYY') : moment()}
                  />
                </div>
              </div>
            )}

            <SecondaryButton
              onClick={() => setIsCalendarOpen((prev) => !prev)}
              classNames='filter-tab_date-picker_button'
              fullWidth={false}
            >
              {isDateChanged && (
                <div
                  className="filter-tab_date-picker_reset-btn"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClearDates(e)
                  }}
                >
                  <button type="button">
                    <RemoveIcon width={20} />
                  </button>
                </div>
              )}

              <span className={`filter-tab_date-picker_label ${isDateChanged ? 'ml-4 ' : ''}`}>
                {t('Event.editEventSeriesModal.setDate')}
              </span>
            </SecondaryButton>
          </div>
        </div>
      </div>

      {eventsState.loading && <p className='text-center'>{t('common.loading')}</p>}

      {!eventsState.loading && (isDateChanged || search) ? (
        <div className='event-list-container'>
          <ul className={classNames('event-list', { 'event-list_loading': eventsState.loading_page })}>
            {eventsState.items
              .filter((event) => !isEventIncluded(event.id))
              .map((event) => {
                const included = isEventIncluded(event.id)
                return (
                  <EventItem
                    key={event.id}
                    event={event}
                    isIncluded={included}
                    isSelecting={isSelecting}
                  />
                )
              })}
          </ul>

          {eventsState.items.length === 0 && (
            <div className='event-list_empty-message-wrapper'>
              <p className='event-list_empty-message'>
                {t('Event.editEventSeriesModal.noEventsFound')}
              </p>
            </div>
          )}

          <Paginator
            page={page}
            totalPages={eventsState.maxPage}
            pageSize={10}
            totalItems={eventsState.total}
            onPageClicked={handlePageChange}
            hideWhenSinglePage
            classNames='mt-3'
          />

          {eventsState.loading_page && <ListLoader />}
        </div>
      ) : (
        <div className='filter-tab_bottom-spacer'/>
      )
      }
    </div>
  )
}

FilterTab.propTypes = {}